import React from "react";
import {
  Autocomplete,
  TextField,
  CircularProgress,
  useTheme,
} from "@mui/material";
import { useAppSelector } from "../../store/hooks";
import { tokens } from "../../theme";
import { useSelector } from "react-redux";
import { RootState } from "../../store/Index";
import type { SxProps } from "@mui/material/styles";

interface CountryOption {
  code: string;
  name: string;
}

interface CountrySelectorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  disabled?: boolean;
  sx?: SxProps;
  error?: boolean;
  helperText?: string;
  placeholder?: string;
  // Optional: include a saved country value that might not be in the Redux list
  savedCountry?: string;
}

const CountrySelector: React.FC<CountrySelectorProps> = ({
  value,
  onChange,
  label = "Country",
  disabled = false,
  sx,
  error = false,
  helperText,
  placeholder = "Select or type country",
  savedCountry,
}) => {
  const countries = useAppSelector((state: RootState) => state.country.countries);
  const loading = useAppSelector((state: RootState) => state.country.loading);
  const countryError = useAppSelector((state: RootState) => state.country.error);

  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);

  const findCountryByValue = React.useCallback((countryValue?: string) => {
    if (!countryValue) return null;
    const normalized = countryValue.trim();
    if (!normalized) return null;

    return (
      countries.find((country) => country.code.toUpperCase() === normalized.toUpperCase()) ||
      countries.find((country) => country.name.toLowerCase() === normalized.toLowerCase()) ||
      null
    );
  }, [countries]);

  const selectedCountry = React.useMemo(() => {
    return findCountryByValue(value) || findCountryByValue(savedCountry || undefined);
  }, [findCountryByValue, savedCountry, value]);

  const availableOptions = React.useMemo(() => {
    const options: CountryOption[] = [...countries];

    if (savedCountry && savedCountry.trim() && !countries.some((country) => {
      const normalizedSaved = savedCountry.trim().toLowerCase();
      return country.code.toLowerCase() === normalizedSaved || country.name.toLowerCase() === normalizedSaved;
    })) {
      options.push({ code: savedCountry.trim(), name: savedCountry.trim() });
    }

    return options;
  }, [countries, savedCountry]);

  const displayError = countryError;

  return (
    <Autocomplete<CountryOption, false, false, false>
      value={selectedCountry ?? (value ? { code: value, name: value } : null)}
      onChange={(_event: React.SyntheticEvent, newValue: CountryOption | null) => {
        if (newValue) {
          onChange(newValue.code);
          return;
        }
        onChange("");
      }}
      options={availableOptions}
      filterOptions={(options, { inputValue }) => {
        const query = inputValue.trim().toLowerCase();
        if (!query) return options;

        return options.filter((option) => {
          return (
            option.name.toLowerCase().includes(query) ||
            option.code.toLowerCase().includes(query)
          );
        });
      }}
      loading={loading}
      disabled={disabled}
      fullWidth
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, value) => option.code === value.code}
      noOptionsText={
        loading
          ? "Loading countries..."
          : availableOptions.length === 0
          ? "No countries available"
          : "No country found"
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          error={error || !!displayError}
          helperText={displayError || helperText}
          sx={sx}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? (
                  <CircularProgress color="inherit" size={20} sx={{ mr: 1 }} />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      sx={{
        "& .MuiAutocomplete-option": {
          color: colors.primary[700],
        },
        "& .MuiAutocomplete-option.Mui-focused": {
          backgroundColor: colors.primary[300] + "20",
        },
        "& .MuiAutocomplete-option[aria-selected='true']": {
          backgroundColor: colors.primary[400] + "20",
        },
        "& .MuiAutocomplete-option[aria-selected='true'].Mui-focused": {
          backgroundColor: colors.primary[400] + "30",
        },
        ...sx,
      }}
    />
  );
};

export default CountrySelector;